<template>

  <el-dialog
    :model-value="modelValue"
    title="产品文件管理（可下载/预览）"
    width="60%"
    @close="handleClose"
  >

    <el-upload
      :headers="headers"
      class="upload-demo"
      drag
      multiple
      :action="getServerUrl() + '/admin/productFile/upload/' + props.productId"
      :show-file-list="false"
      :on-success="handleUploadSuccess"
      :on-error="handleUploadError"
      :before-upload="beforeUpload"
    >
      <el-icon class="el-icon--upload"><upload-filled /></el-icon>
      <div class="el-upload__text">将文件拖到此处，或<em>点击上传</em></div>
      <template #tip>
        <div class="el-upload__tip">
          仅支持 doc/docx/xls/xlsx/pdf/ppt/pptx 格式，单文件不超过 50MB
        </div>
      </template>
    </el-upload>

    <el-table :data="tableData" stripe style="width: 100%; margin-top: 15px">
      <el-table-column type="index" width="50" />
      <el-table-column prop="originalName" label="文件名" min-width="200" show-overflow-tooltip />
      <el-table-column label="类型" width="100">
        <template v-slot="scope">
          <el-tag :type="(typeMeta[scope.row.fileType] || {}).color || 'info'" disable-transitions>
            {{ (typeMeta[scope.row.fileType] || {}).label || scope.row.fileType }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="大小" width="110">
        <template v-slot="scope">{{ formatSize(scope.row.fileSize) }}</template>
      </el-table-column>
      <el-table-column prop="createTime" label="上传时间" width="160" />
      <el-table-column label="操作" width="160">
        <template v-slot="scope">
          <el-button type="primary" :icon="Download" @click="handleDownload(scope.row)"></el-button>
          <el-button type="danger" :icon="Delete" @click="handleRemove(scope.$index)"></el-button>
        </template>
      </el-table-column>
    </el-table>

    <template #footer>
      <el-button @click="handleClose">取消</el-button>
      <el-button type="primary" @click="handleSave">保存</el-button>
    </template>

  </el-dialog>

</template>

<script setup>
import { ref, watch } from 'vue'
import axios from "@/util/axios";
import rawAxios from 'axios';
import { ElMessage, ElMessageBox } from "element-plus";
import { getServerUrl } from "@/config/sys";
import { Delete, Download, UploadFilled } from '@element-plus/icons-vue'

const props = defineProps({
  productId: { type: Number, default: -1, required: true },
  modelValue: { type: Boolean, default: false }
})

const emits = defineEmits(['update:modelValue'])

// 文件类型图标元数据（颜色 + 显示名）
const typeMeta = {
  pdf:  { color: 'danger',  label: 'PDF' },
  doc:  { color: 'primary', label: 'Word' },
  docx: { color: 'primary', label: 'Word' },
  xls:  { color: 'success', label: 'Excel' },
  xlsx: { color: 'success', label: 'Excel' },
  ppt:  { color: 'warning', label: 'PPT' },
  pptx: { color: 'warning', label: 'PPT' }
}

// 允许的文件后缀白名单
const ALLOWED_TYPE = ['doc', 'docx', 'xls', 'xlsx', 'pdf', 'ppt', 'pptx']
const MAX_SIZE = 50 * 1024 * 1024

const headers = ref({
  token: window.sessionStorage.getItem('token')
})

const tableData = ref([])

const initProductFileList = async () => {
  if (props.productId && props.productId !== -1) {
    const res = await axios.get("admin/productFile/list/" + props.productId)
    tableData.value = res.data.productFileList || []
  }
}

watch(
  () => props.productId,
  () => {
    tableData.value = []
    initProductFileList()
  },
  { immediate: true }
)

const beforeUpload = (file) => {
  const name = file.name || ''
  const idx = name.lastIndexOf('.')
  const suffix = idx >= 0 ? name.substring(idx + 1).toLowerCase() : ''
  if (!ALLOWED_TYPE.includes(suffix)) {
    ElMessage.error('仅支持 doc/docx/xls/xlsx/pdf/ppt/pptx 格式文件！')
    return false
  }
  if (file.size > MAX_SIZE) {
    ElMessage.error('文件大小不能超过 50MB！')
    return false
  }
  return true
}

const handleUploadSuccess = (res) => {
  if (res.code === 0 && res.data) {
    tableData.value.push(res.data)
  } else {
    ElMessage.error(res.msg || '上传失败')
  }
}

const handleUploadError = () => {
  ElMessage.error('上传失败，请稍后重试')
}

const handleRemove = (index) => {
  ElMessageBox.confirm('您确定要移除该文件吗？', '系统提示', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    type: 'warning'
  }).then(() => {
    tableData.value.splice(index, 1)
    ElMessage.success('已移除，保存后生效')
  }).catch(() => {})
}

const handleDownload = async (row) => {
  if (!row || !row.id) {
    ElMessage.error('文件信息异常')
    return
  }
  try {
    const res = await rawAxios.get(getServerUrl() + '/admin/productFile/download/' + row.id, {
      responseType: 'blob',
      headers: { token: window.sessionStorage.getItem('token') }
    })
    if (res.status !== 200) {
      ElMessage.error('下载失败')
      return
    }
    const url = window.URL.createObjectURL(new Blob([res.data]))
    const a = document.createElement('a')
    a.href = url
    a.download = row.originalName || 'file'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)
  } catch (e) {
    ElMessage.error('下载失败')
  }
}

const handleSave = async () => {
  if (props.productId === -1) {
    ElMessage.error('产品信息异常')
    return
  }
  let res = await axios.post("admin/productFile/save/" + props.productId, tableData.value)
  if (res.data.code === 0) {
    ElMessage.success('保存成功')
    handleClose()
  } else {
    ElMessage.error(res.data.msg || '保存失败')
  }
}

const handleClose = () => {
  emits('update:modelValue', false)
}

const formatSize = (size) => {
  if (size == null) return '-'
  if (size < 1024) return size + ' B'
  if (size < 1024 * 1024) return (size / 1024).toFixed(1) + ' KB'
  return (size / 1024 / 1024).toFixed(1) + ' MB'
}
</script>

<style>
.el-icon--upload {
  font-size: 40px;
  color: #8c939d;
}
</style>